import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import PropTypes from "prop-types";
import MiniTrajectoryChart from "./MiniTrajectoryChart";

const TrajectoryTab = ({
  dailySpend,
  projectedTotal,
  avgDailySpend,
  formatDate,
  formatCurrency,
  daysUntilNegative,
  currentDay,
  daysInMonth,
  remainingBalance,
  projectedDrawdown,
  showProjections = false,
}) => {
  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-2 sm:mb-0">
          Daily Spending
        </h2>
        <div className="text-left sm:text-right">
          <p className="text-sm text-gray-500">
            {showProjections ? "Projected Monthly Total" : "Period Total"}
          </p>
          <p className="text-xl font-bold text-purple-700">
            {formatCurrency(projectedTotal)}
          </p>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart
          data={dailySpend}
          margin={{ top: 5, right: 30, left: 20, bottom: 65 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            angle={-45}
            textAnchor="end"
            height={80}
            tickFormatter={(date) => formatDate(date)}
            dy={10}
          />
          <YAxis tickFormatter={(value) => formatCurrency(value)} />
          <Tooltip
            formatter={(value) => formatCurrency(value)}
            labelFormatter={(label) => formatDate(label)}
          />
          <Legend verticalAlign="top" />
          <Line
            type="monotone"
            dataKey="amount"
            stroke="#8884d8"
            activeDot={{ r: 8 }}
            name="Spending"
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-6 p-4 bg-purple-50 rounded-lg">
        <h3 className="font-semibold text-purple-700 mb-2">
          Spending Insights
        </h3>
        {showProjections ? (
          <p className="text-gray-700">
            Based on your current spending pattern of approximately
            <span className={"font-medium"}>
              {" "}
              {formatCurrency(avgDailySpend)}{" "}
            </span>
            per day, you&apos;re projected to spend
            <span className={"font-medium"}>
              {" "}
              {formatCurrency(projectedTotal)}{" "}
            </span>
            by the end of the month.
          </p>
        ) : (
          <p className="text-gray-700">
            Showing daily spending for the selected period. Month-to-date
            projections are available when viewing &quot;This Month&quot;.
          </p>
        )}
      </div>

      {/* Balance Trajectory Chart */}
      {showProjections && (
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-semibold text-blue-700 mb-4">
          Balance Trajectory
        </h3>
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <MiniTrajectoryChart
            daysUntilNegative={daysUntilNegative}
            currentDay={currentDay}
            daysInMonth={daysInMonth}
            remainingBalance={remainingBalance}
            projectedDrawdown={projectedDrawdown}
          />
        </div>
        <p className="text-gray-700 mt-3">
          {daysUntilNegative && daysUntilNegative > 0 ? (
            <>
              Your account balance is projected to go negative on day{" "}
              <span className="font-medium text-red-600">{currentDay + daysUntilNegative}</span>{" "}
              of the month, with a potential drawdown of{" "}
              <span className="font-medium text-red-600">
                {formatCurrency(Math.abs(projectedDrawdown))}
              </span>
              .
            </>
          ) : daysUntilNegative === -1 ? (
            <span className="text-red-600 font-medium">
              Your balance has already gone negative this month.
            </span>
          ) : (
            <span className="text-green-600 font-medium">
              Your balance is projected to remain positive throughout the month.
            </span>
          )}
        </p>
      </div>
      )}
    </div>
  );
};

TrajectoryTab.propTypes = {
  dailySpend: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }),
  ).isRequired,
  projectedTotal: PropTypes.number.isRequired,
  avgDailySpend: PropTypes.number.isRequired,
  formatDate: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  daysUntilNegative: PropTypes.number,
  currentDay: PropTypes.number.isRequired,
  daysInMonth: PropTypes.number.isRequired,
  remainingBalance: PropTypes.number,
  projectedDrawdown: PropTypes.number,
  showProjections: PropTypes.bool,
};

export default TrajectoryTab;
